import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { MapPin, Users, Phone, Mail, RefreshCw } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon
const engineerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// UK postcode to approximate coordinates mapping (major postcode areas)
const postcodeCoordinates = {
  // London
  'SW': [51.4613, -0.1870],
  'SE': [51.4500, -0.0500],
  'NW': [51.5500, -0.2000],
  'N': [51.5700, -0.1000],
  'E': [51.5400, 0.0500],
  'W': [51.5100, -0.2000],
  'EC': [51.5150, -0.1000],
  'WC': [51.5150, -0.1200],
  // Major cities
  'B': [52.4862, -1.8904], // Birmingham
  'M': [53.4808, -2.2426], // Manchester
  'L': [53.4084, -2.9916], // Liverpool
  'S': [53.3811, -1.4701], // Sheffield
  'LS': [53.8008, -1.5491], // Leeds
  'G': [55.8642, -4.2518], // Glasgow
  'EH': [55.9533, -3.1883], // Edinburgh
  'CF': [51.4816, -3.1791], // Cardiff
  'BS': [51.4545, -2.5879], // Bristol
  'NG': [52.9548, -1.1581], // Nottingham
  'LE': [52.6369, -1.1398], // Leicester
  'CV': [52.4068, -1.5197], // Coventry
  'NE': [54.9783, -1.6178], // Newcastle
  'SR': [54.9069, -1.3838], // Sunderland
  'BN': [50.8225, -0.1372], // Brighton
  'SO': [50.9097, -1.4044], // Southampton
  'PO': [50.8198, -1.0880], // Portsmouth
  'OX': [51.7520, -1.2577], // Oxford
  'CB': [52.2053, 0.1218],  // Cambridge
  'MK': [52.0406, -0.7594], // Milton Keynes
  'RG': [51.4543, -0.9781], // Reading
  'SL': [51.5105, -0.5950], // Slough
  'LU': [51.8787, -0.4200], // Luton
  'AL': [51.7529, -0.3389], // St Albans
  'WD': [51.6565, -0.3903], // Watford
  'HA': [51.5802, -0.3340], // Harrow
  'UB': [51.5328, -0.4770], // Uxbridge
  'TW': [51.4491, -0.3263], // Twickenham
  'KT': [51.3782, -0.2814], // Kingston
  'SM': [51.3618, -0.1945], // Sutton
  'CR': [51.3714, -0.0977], // Croydon
  'BR': [51.4039, 0.0198],  // Bromley
  'DA': [51.4469, 0.2118],  // Dartford
  'RM': [51.5775, 0.1827],  // Romford
  'IG': [51.5590, 0.0741],  // Ilford
  'EN': [51.6538, -0.0799], // Enfield
  // More areas
  'BD': [53.7960, -1.7594], // Bradford
  'HX': [53.7180, -1.8563], // Halifax
  'HD': [53.6458, -1.7850], // Huddersfield
  'WF': [53.6833, -1.4977], // Wakefield
  'DN': [53.5228, -1.1288], // Doncaster
  'HU': [53.7676, -0.3274], // Hull
  'YO': [53.9591, -1.0815], // York
  'DL': [54.5246, -1.5577], // Darlington
  'TS': [54.5742, -1.2350], // Teesside
  'DH': [54.7761, -1.5733], // Durham
  'CA': [54.8951, -2.9382], // Carlisle
  'LA': [54.0484, -2.7990], // Lancaster
  'PR': [53.7632, -2.7031], // Preston
  'BB': [53.7500, -2.4833], // Blackburn
  'BL': [53.5833, -2.4333], // Bolton
  'OL': [53.5444, -2.1169], // Oldham
  'WN': [53.5500, -2.6333], // Wigan
  'SK': [53.3889, -2.1278], // Stockport
  'WA': [53.3900, -2.5967], // Warrington
  'CH': [53.1905, -2.8931], // Chester
  'CW': [53.0977, -2.4406], // Crewe
  'ST': [53.0027, -2.1794], // Stoke-on-Trent
  'DE': [52.9225, -1.4746], // Derby
  'WS': [52.5858, -1.9821], // Walsall
  'WV': [52.5870, -2.1288], // Wolverhampton
  'DY': [52.5119, -2.0867], // Dudley
  'TF': [52.6766, -2.4469], // Telford
  'SY': [52.7077, -2.7540], // Shrewsbury
  'HR': [52.0567, -2.7160], // Hereford
  'WR': [52.1936, -2.2216], // Worcester
  'GL': [51.8642, -2.2382], // Gloucester
  'SN': [51.5588, -1.7819], // Swindon
  'BA': [51.3758, -2.3599], // Bath
  'TA': [51.0148, -3.1031], // Taunton
  'EX': [50.7184, -3.5339], // Exeter
  'TQ': [50.4619, -3.5253], // Torquay
  'PL': [50.3755, -4.1427], // Plymouth
  'TR': [50.2632, -5.0527], // Truro
  'DT': [50.7131, -2.4403], // Dorchester
  'BH': [50.7192, -1.8808], // Bournemouth
  'SP': [51.0688, -1.7945], // Salisbury
  'GU': [51.2362, -0.7714], // Guildford
  'RH': [51.2020, -0.2008], // Redhill
  'TN': [51.1320, 0.2637],  // Tonbridge
  'ME': [51.2722, 0.5276],  // Medway
  'CT': [51.2802, 1.0789],  // Canterbury
  'SS': [51.5406, 0.7077],  // Southend
  'CM': [51.7343, 0.4691],  // Chelmsford
  'CO': [51.8959, 0.8919],  // Colchester
  'IP': [52.0567, 1.1482],  // Ipswich
  'NR': [52.6309, 1.2974],  // Norwich
  'PE': [52.5695, -0.2405], // Peterborough
  'LN': [53.2307, -0.5406], // Lincoln
  'HG': [53.9921, -1.5418], // Harrogate
  'DD': [56.4620, -2.9707], // Dundee
  'AB': [57.1497, -2.0943], // Aberdeen
  'IV': [57.4778, -4.2247], // Inverness
  'PH': [56.3950, -3.4308], // Perth
  'FK': [56.0019, -3.7839], // Falkirk
  'KY': [56.1165, -3.1596], // Kirkcaldy
  'PA': [55.8456, -4.4239], // Paisley
  'ML': [55.7736, -3.9940], // Motherwell
  'KA': [55.4589, -4.6290], // Kilmarnock
  'DG': [55.0711, -3.6052], // Dumfries
  'SA': [51.6214, -3.9436], // Swansea
  'NP': [51.5842, -2.9977], // Newport
  'LD': [52.2488, -3.3810], // Llandrindod Wells
  'LL': [53.2274, -3.8278], // Llandudno
  'SY': [52.4128, -4.0811], // Aberystwyth
  'BT': [54.5973, -5.9301], // Belfast
};

// Fallback: Get approximate coordinates from postcode prefix (used only if
// the server didn't supply lat/lng — e.g. an engineer added before caching was
// enabled, or postcodes.io was unreachable server-side).
const getCoordinatesFromPrefix = (postcode) => {
  if (!postcode) return null;
  
  // Extract prefix (e.g., "SW1" -> "SW", "M1" -> "M")
  const prefix = postcode.replace(/[0-9]/g, '').toUpperCase().trim();
  
  // Try exact match first
  if (postcodeCoordinates[prefix]) {
    return [postcodeCoordinates[prefix][0], postcodeCoordinates[prefix][1]];
  }
  
  // Try first two letters
  const firstTwo = prefix.substring(0, 2);
  if (postcodeCoordinates[firstTwo]) {
    return [postcodeCoordinates[firstTwo][0], postcodeCoordinates[firstTwo][1]];
  }
  
  // Try first letter
  const firstOne = prefix.substring(0, 1);
  if (postcodeCoordinates[firstOne]) {
    return [postcodeCoordinates[firstOne][0], postcodeCoordinates[firstOne][1]];
  }
  
  return null;
};

// Component to fit bounds when engineers change
const FitBounds = ({ engineers }) => {
  const map = useMap();
  
  useEffect(() => {
    if (engineers.length > 0) {
      const bounds = engineers
        .map(e => e.coordinates)
        .filter(c => c !== null);
      
      if (bounds.length > 0) {
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 10 });
      }
    }
  }, [engineers, map]);
  
  return null;
};

const EngineerMap = ({ onClose }) => {
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAllEngineers();
  }, []);

  const fetchAllEngineers = async (attempt = 1) => {
    try {
      const response = await axios.get(`${API}/engineers/all-locations`, { timeout: 45000 });
      if (response.data.success) {
        const engineersData = response.data.engineers || [];

        // Prefer server-supplied coordinates; fall back to prefix lookup only if missing
        const engineersWithCoords = engineersData.map((engineer) => {
          let coordinates = null;
          if (engineer.latitude != null && engineer.longitude != null) {
            coordinates = [engineer.latitude, engineer.longitude];
          } else {
            const markerPostcode = engineer.businessPostcode || engineer.postcodes?.[0] || '';
            coordinates = getCoordinatesFromPrefix(markerPostcode);
          }
          return {
            ...engineer,
            coordinates,
            primaryPostcode: engineer.businessPostcode || engineer.postcodes?.[0] || ''
          };
        });

        // Keep only engineers we could plot
        setEngineers(engineersWithCoords.filter(e => e.coordinates !== null));
        setError(null);
        setLoading(false);
        return;
      }
      // Success:false path — surface a clear message
      setError(response.data.detail || response.data.error || 'Engineer locations could not be loaded right now.');
      setLoading(false);
    } catch (err) {
      console.error(`Error fetching engineers (attempt ${attempt}):`, err?.message || err);
      // Retry once on transient failure
      if (attempt < 2) {
        await new Promise(r => setTimeout(r, 1000));
        return fetchAllEngineers(attempt + 1);
      }
      setError('Failed to load engineer locations. Please refresh.');
      setLoading(false);
    }
  };

  // UK center coordinates
  const ukCenter = [54.0, -2.5];

  if (loading) {
    return (
      <div className="engineer-map-container">
        <div className="map-header">
          <h3><MapPin size={24} /> Engineer Coverage Map</h3>
          {onClose && (
            <button className="map-close-btn" onClick={onClose}>×</button>
          )}
        </div>
        <div className="map-loading">
          <div className="loading-spinner"></div>
          <p>Loading engineer locations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="engineer-map-container">
        <div className="map-header">
          <h3><MapPin size={24} /> Engineer Coverage Map</h3>
          {onClose && (
            <button className="map-close-btn" onClick={onClose}>×</button>
          )}
        </div>
        <div className="map-error" data-testid="map-error">
          <p>{error}</p>
          <button
            type="button"
            className="map-retry-btn"
            onClick={() => { setError(null); setLoading(true); fetchAllEngineers(1); }}
            data-testid="map-retry-btn"
          >
            <RefreshCw size={16} /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="engineer-map-container" data-testid="engineer-map">
      <div className="map-header">
        <div className="map-title-section">
          <h3><MapPin size={24} /> Engineer Coverage Map</h3>
          <span className="engineer-count">
            <Users size={16} /> {engineers.length} {engineers.length === 1 ? 'engineer' : 'engineers'} across the UK
          </span>
        </div>
        {onClose && (
          <button className="map-close-btn" onClick={onClose} data-testid="map-close-btn">×</button>
        )}
      </div>
      
      <div className="map-wrapper">
        <MapContainer
          center={ukCenter}
          zoom={6}
          style={{ height: '500px', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds engineers={engineers} />
          
          {engineers.map((engineer, index) => (
            <Marker 
              key={engineer.id || index} 
              position={engineer.coordinates}
              icon={engineerIcon}
            >
              <Popup>
                <div className="map-popup">
                  <h4>{engineer.fullName}</h4>
                  <p className="popup-business">{engineer.businessName}</p>
                  <p className="popup-coverage">
                    <strong>Coverage:</strong> {engineer.postcodes?.join(', ')}
                  </p>
                  {engineer.phone && (
                    <a href={`tel:${engineer.phone.replace(/\s/g, '')}`} className="popup-contact">
                      <Phone size={14} /> {engineer.phone}
                    </a>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      
      <div className="map-legend">
        <p>
          <span className="legend-marker">📍</span> 
          Each marker is a verified Gas Safe engineer on The Gas Guide. 
          Search by your postcode above to see the engineers covering your area.
        </p>
      </div>
    </div>
  );
};

export default EngineerMap;
